import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Save, Plus, Trash2 } from 'lucide-react';
import usePageContent from './usePageContent';
import PageHeroEditor from './PageHeroEditor';
import LivePreviewPanel, { AffiliatesPreview } from './LivePreviewPanel';

const defaults = {
  hero: {
    title: 'Our',
    highlight: 'Affiliates',
    description:
      'Partner associations working together to strengthen the voice of public employees across Papua New Guinea',
  },
  intro: {
    heading: 'Stronger Together',
    body_markdown:
      'PEA PNG is proud to work alongside specialized associations that represent specific sectors within the public service.',
  },
  benefits: {
    heading: 'Partnership Benefits',
    items: [
      'Collective Strength - Unified voice in advocating for public employee rights',
      'Shared Resources - Access to legal services, training programs, and organizational support',
      'Network Growth - Expanded networking opportunities and knowledge sharing',
    ],
  },
  cta: {
    heading: 'Interested in Becoming an Affiliate?',
    body_markdown: 'Join us in strengthening the public service sector across Papua New Guinea',
    button_text: 'Contact Us',
    button_link: '/contact',
  },
};

const AffiliatesContentEditor = () => {
  const [showPreview, setShowPreview] = useState(false);
  const { loading, saving, sections, setSections, saveSection } = usePageContent({
    pageName: 'affiliates',
    defaultsBySection: defaults,
  });

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner"></div>
      </div>
    );
  }

  const addBenefit = () => {
    const newItems = [...(sections.benefits.items || []), 'New Benefit - Description'];
    setSections({ ...sections, benefits: { ...sections.benefits, items: newItems } });
  };

  const removeBenefit = (idx) => {
    const newItems = sections.benefits.items.filter((_, i) => i !== idx);
    setSections({ ...sections, benefits: { ...sections.benefits, items: newItems } });
  };

  return (
    <div className="space-y-6">
      {/* Live Preview Panel */}
      <LivePreviewPanel 
        pageName="affiliates" 
        showPreview={showPreview} 
        setShowPreview={setShowPreview}
      >
        <AffiliatesPreview content={sections} />
      </LivePreviewPanel>

      <PageHeroEditor
        title={sections.hero.title}
        setTitle={(v) => setSections({ ...sections, hero: { ...sections.hero, title: v } })}
        highlight={sections.hero.highlight}
        setHighlight={(v) => setSections({ ...sections, hero: { ...sections.hero, highlight: v } })}
        description={sections.hero.description}
        setDescription={(v) => setSections({ ...sections, hero: { ...sections.hero, description: v } })}
        onSave={() => saveSection('hero')}
        saving={saving}
      />

      <Card>
        <CardHeader>
          <CardTitle>Intro</CardTitle>
          <CardDescription>Markdown supported</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={sections.intro.heading}
              onChange={(e) => setSections({ ...sections, intro: { ...sections.intro, heading: e.target.value } })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Body (Markdown)</Label>
            <Textarea
              value={sections.intro.body_markdown}
              onChange={(e) => setSections({ ...sections, intro: { ...sections.intro, body_markdown: e.target.value } })}
              className="mt-1"
              rows={4}
            />
          </div>
          <Button
            type="button"
            onClick={() => saveSection('intro')}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save Intro
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Partnership Benefits</CardTitle>
          <CardDescription>List of benefits for affiliates (format: "Title - Description")</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Section Heading</Label>
            <Input
              value={sections.benefits.heading}
              onChange={(e) => setSections({ ...sections, benefits: { ...sections.benefits, heading: e.target.value } })}
              className="mt-1"
            />
          </div>

          {(sections.benefits.items || []).map((item, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={item}
                onChange={(e) => {
                  const newItems = [...sections.benefits.items];
                  newItems[idx] = e.target.value;
                  setSections({ ...sections, benefits: { ...sections.benefits, items: newItems } });
                }}
                placeholder="Title - Description"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeBenefit(idx)}
                className="text-red-500 hover:text-red-600"
              >
                <Trash2 size={18} />
              </Button>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            onClick={addBenefit}
            className="w-full"
          >
            <Plus className="mr-2" size={18} /> Add Benefit
          </Button>

          <Button
            type="button"
            onClick={() => saveSection('benefits')}
            disabled={saving}
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          >
            <Save className="mr-2" size={18} /> Save Benefits
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>CTA Section</CardTitle>
          <CardDescription>Call to action at the bottom of the page</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Heading</Label>
            <Input
              value={sections.cta.heading}
              onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, heading: e.target.value } })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Body (Markdown)</Label>
            <Textarea
              value={sections.cta.body_markdown}
              onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, body_markdown: e.target.value } })}
              className="mt-1"
              rows={2}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Button Text</Label>
              <Input
                value={sections.cta.button_text}
                onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, button_text: e.target.value } })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Button Link</Label>
              <Input
                value={sections.cta.button_link}
                onChange={(e) => setSections({ ...sections, cta: { ...sections.cta, button_link: e.target.value } })}
                className="mt-1"
              />
            </div>
          </div>
          <Button
            type="button"
            onClick={() => saveSection('cta')}
            disabled={saving}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
          >
            <Save className="mr-2" size={18} /> Save CTA
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliatesContentEditor;
